/**
 * TanStack Query hooks and the query-key vocabulary.
 *
 * Keys are built here rather than typed at call sites, because invalidation is only
 * correct if the key that writes and the key that reads agree exactly. A mutation
 * invalidating `['products']` while a screen reads `['product-list']` produces a stale
 * screen that a refresh fixes — the kind of bug that gets reported as "sometimes it
 * doesn't save".
 */

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api, qs, type Paginated } from './api.js';
import { toast } from './stores.js';
import { friendlyError } from '@serendib/shared';

/* ========================================================================== */
/* Keys                                                                       */
/* ========================================================================== */

export const keys = {
  bootstrap: ['bootstrap'] as const,
  page: (slug: string) => ['page', slug] as const,

  projects: (params?: unknown) => ['projects', params ?? {}] as const,
  project: (slug: string) => ['project', slug] as const,
  services: () => ['services'] as const,
  service: (slug: string) => ['service', slug] as const,
  posts: (params?: unknown) => ['posts', params ?? {}] as const,
  post: (slug: string) => ['post', slug] as const,

  shopFilters: ['shop', 'filters'] as const,
  shopProducts: (params: unknown) => ['shop', 'products', params] as const,
  product: (slug: string) => ['shop', 'product', slug] as const,
  quote: (lines: unknown) => ['checkout', 'quote', lines] as const,

  admin: {
    dashboard: ['admin', 'dashboard'] as const,
    list: (resource: string, params?: unknown) => ['admin', resource, params ?? {}] as const,
    one: (resource: string, id: number | string) => ['admin', resource, 'one', id] as const,
    history: (entity: string, id: number) => ['admin', 'history', entity, id] as const,
    settings: ['admin', 'settings'] as const,
    emailStatus: ['admin', 'email', 'status'] as const,
    pageDetail: (id: number) => ['admin', 'pages', id] as const,
    sectionTypes: ['admin', 'section-types'] as const,
    stock: (productId: number) => ['admin', 'stock', productId] as const,
  },
};

/* ========================================================================== */
/* Public site                                                                */
/* ========================================================================== */

export interface NavItem {
  id: number;
  location: string;
  label: string;
  href: string;
  parentId: number | null;
  opensInNewTab: boolean;
}

export interface BootstrapData {
  settings: Record<string, unknown>;
  nav: NavItem[];
}

/**
 * Settings and navigation — the chrome's whole data requirement, in one request.
 *
 * A long `staleTime` because this is the same answer for every visitor and changes a
 * few times a year. Without it, every route change re-fetches the footer.
 */
export const bootstrapQuery = () =>
  ({
    queryKey: keys.bootstrap,
    queryFn: () => api.get<BootstrapData>('/public/bootstrap', { auth: false }),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

export function useBootstrap() {
  return useQuery(bootstrapQuery());
}

/**
 * Read one setting with a typed fallback.
 *
 * The fallback matters more than it looks: this is called during the FIRST render,
 * before `/public/bootstrap` has answered, so every caller would otherwise have to
 * handle `undefined` — and the one that forgets renders "undefined" into the footer.
 */
export function setting<T>(data: BootstrapData | undefined, key: string, fallback: T): T {
  const value = data?.settings?.[key];
  return value === undefined || value === null ? fallback : (value as T);
}

/**
 * The WhatsApp chat link, or null when no number is configured.
 *
 * Null is the important half. WhatsApp is the channel most Sri Lankan buyers reach for
 * first, but a link built from a number that has no WhatsApp account opens a dead chat —
 * which is worse than not offering it, because the visitor has already decided how they
 * want to talk to you. Every caller renders nothing when this returns null.
 *
 * Deliberately NOT derived from `contact.phone`: the office line is a landline and
 * WhatsApp runs on a mobile.
 */
export function whatsappLink(data: BootstrapData | undefined): string | null {
  // Tolerant of however it was typed — +94 77 123 4567, spaces, dashes, brackets — because
  // `wa.me` accepts digits only and a copy-pasted number is the normal case.
  const digits = setting(data, 'contact.whatsapp', '').replace(/\D/g, '');
  if (digits.length < 8) return null;

  const message = setting(data, 'contact.whatsapp_message', '');
  return message
    ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
    : `https://wa.me/${digits}`;
}

/**
 * A "get directions" link for the office address.
 *
 * A LINK rather than an embedded map, by default. An embedded Google map sends every
 * visitor's IP and referrer to Google on page load, whether or not they care where the
 * office is; a link sends nothing until somebody clicks it. The client can still opt into
 * the embed by filling in `contact.map_embed_url`.
 */
export function directionsLink(data: BootstrapData | undefined): string | null {
  const address = [
    setting(data, 'contact.address_line1', ''),
    setting(data, 'contact.address_line2', ''),
  ].filter(Boolean).join(', ');
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

export interface SectionData {
  id: number;
  type: string;
  payload: Record<string, unknown>;
  adminLabel: string | null;
  isEnabled: boolean;
  sortOrder: number;
  resolved?: unknown;
}

export interface PageData {
  page: {
    id: number;
    slug: string;
    title: string;
    status: string;
    seoTitle: string | null;
    seoDescription: string | null;
    ogImageUrl: string | null;
  };
  sections: SectionData[];
}

export const pageQuery = (slug: string) =>
  ({
    queryKey: keys.page(slug),
    queryFn: () => api.get<PageData>(`/public/pages/${slug}`),
    staleTime: 60_000,
    // A missing page is a 404 route, not a transient failure. Retrying three times
    // makes the not-found screen take four seconds to appear.
    retry: (count: number, error: unknown) => count < 2 && (error as { status?: number }).status !== 404,
  });

export function usePage(slug: string) {
  return useQuery(pageQuery(slug));
}

export interface ProjectCard {
  id: number;
  slug: string;
  title: string;
  client: string | null;
  summary: string;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  services: string[];
  technologies: string[];
  outcomes: string[];
  year: number | null;
  isFeatured: boolean;
}

export const projectsQuery = (params: { page?: number; pageSize?: number; service?: string } = {}) =>
  ({
    queryKey: keys.projects(params),
    queryFn: () => api.get<Paginated<ProjectCard>>(`/public/projects${qs(params)}`),
    staleTime: 60_000,
  });

export function useProjects(params: { page?: number; pageSize?: number; service?: string } = {}) {
  return useQuery(projectsQuery(params));
}

export const projectQuery = (slug: string) =>
  ({
    queryKey: keys.project(slug),
    queryFn: () => api.get<{ project: Record<string, unknown>; related: ProjectCard[] }>(`/public/projects/${slug}`),
    staleTime: 60_000,
    retry: (count: number, error: unknown) => count < 2 && (error as { status?: number }).status !== 404,
  });

export function useProject(slug: string) {
  return useQuery(projectQuery(slug));
}

export interface ServiceCard {
  id: number;
  slug: string;
  title: string;
  summary: string;
  icon: string | null;
  imageUrl: string | null;
  bullets: string[];
}

export const servicesQuery = () =>
  ({
    queryKey: keys.services(),
    queryFn: () => api.get<ServiceCard[]>('/public/services'),
    staleTime: 5 * 60_000,
  });

export function useServices() {
  return useQuery(servicesQuery());
}

export const serviceQuery = (slug: string) =>
  ({
    queryKey: keys.service(slug),
    queryFn: () => api.get<{ service: Record<string, unknown>; projects: ProjectCard[] }>(`/public/services/${slug}`),
    staleTime: 60_000,
    retry: (count: number, error: unknown) => count < 2 && (error as { status?: number }).status !== 404,
  });

export function useService(slug: string) {
  return useQuery(serviceQuery(slug));
}

export interface PostCard {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  coverImageUrl: string | null;
  category: string | null;
  authorName: string | null;
  publishedAt: string | null;
}

export const postsQuery = (params: { page?: number; pageSize?: number; category?: string } = {}) =>
  ({
    queryKey: keys.posts(params),
    queryFn: () => api.get<Paginated<PostCard>>(`/public/posts${qs(params)}`),
    staleTime: 60_000,
  });

export function usePosts(params: { page?: number; pageSize?: number; category?: string } = {}) {
  return useQuery(postsQuery(params));
}

export const postQuery = (slug: string) =>
  ({
    queryKey: keys.post(slug),
    queryFn: () => api.get<{ post: Record<string, unknown>; related: PostCard[] }>(`/public/posts/${slug}`),
    staleTime: 60_000,
    retry: (count: number, error: unknown) => count < 2 && (error as { status?: number }).status !== 404,
  });

export function usePost(slug: string) {
  return useQuery(postQuery(slug));
}

/* ========================================================================== */
/* Store                                                                      */
/* ========================================================================== */

export interface ProductCard {
  id: number;
  sku: string;
  slug: string;
  name: string;
  shortDescription: string | null;
  price: string;
  compareAtPrice: string | null;
  currency: string;
  brandName: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  imageUrl: string | null;
  imageAlt: string | null;
  isFeatured: boolean;
  allowBackorder: boolean;
  available: number;
  stockState: 'in-stock' | 'low-stock' | 'out-of-stock' | 'backorder';
}

export interface ShopFilters {
  categories: { id: number; slug: string; name: string; parent_id: number | null; product_count: number }[];
  brands: { id: number; slug: string; name: string; logoUrl: string | null }[];
  priceRange: { min: string; max: string };
}

export const shopFiltersQuery = () =>
  ({
    queryKey: keys.shopFilters,
    queryFn: () => api.get<ShopFilters>('/public/shop/filters', { auth: false }),
    staleTime: 5 * 60_000,
  });

export function useShopFilters() {
  return useQuery(shopFiltersQuery());
}

export const shopProductsQuery = (params: Record<string, unknown>) =>
  ({
    queryKey: keys.shopProducts(params),
    queryFn: () => api.get<Paginated<ProductCard>>(`/public/shop/products${qs(params)}`),
    // Availability is on this response, so it must not go stale for long — showing
    // "in stock" for something sold out ten minutes ago is a promise the checkout
    // will then break.
    staleTime: 20_000,
    placeholderData: (previous: Paginated<ProductCard> | undefined) => previous,
  });

export function useShopProducts(params: Record<string, unknown>) {
  return useQuery(shopProductsQuery(params));
}

export interface ProductDetail extends ProductCard {
  description: string | null;
  /** Per-record SEO overrides. Null means "use the name and short description". */
  seoTitle: string | null;
  seoDescription: string | null;
  ogImageUrl: string | null;
  model: string | null;
  specs: { label: string; value: string }[];
  highlights: string[];
  warrantyText: string | null;
  images: { id: number; url: string; alt: string | null }[];
  related: ProductCard[];
  brandSlug: string | null;
}

export const productQuery = (slug: string) =>
  ({
    queryKey: keys.product(slug),
    queryFn: () => api.get<ProductDetail>(`/public/shop/products/${slug}`),
    staleTime: 20_000,
    retry: (count: number, error: unknown) => count < 2 && (error as { status?: number }).status !== 404,
  });

export function useProduct(slug: string) {
  return useQuery(productQuery(slug));
}

export interface QuoteLine {
  productId: number;
  sku: string;
  slug: string;
  name: string;
  /** Null where the product is filed under nothing, which the back office allows. */
  categorySlug: string | null;
  unitPrice: string;
  quantity: number;
  available: number;
  allowBackorder: boolean;
}

export interface QuoteResult {
  lines: QuoteLine[];
  unavailable: { productId: number; reason: string }[];
  taxRate: number;
  shippingFlat: string;
  freeShippingOver: string | null;
  currency: string;
}

/**
 * Re-price the cart against the server.
 *
 * The cart lives in the browser and can be days old. This is what makes the cart page
 * show today's price and today's availability rather than whatever was true when the
 * item was added — and it is a POST because the cart is a body, not a URL.
 */
export function useCartQuote(lines: { productId: number; quantity: number }[]) {
  return useQuery({
    queryKey: keys.quote(lines),
    queryFn: () => api.post<QuoteResult>('/checkout/quote', { lines }, { auth: false }),
    enabled: lines.length > 0,
    staleTime: 15_000,
  });
}

/* ========================================================================== */
/* Mutations                                                                  */
/* ========================================================================== */

/**
 * A mutation with the two things every mutation in this app needs: a toast on failure
 * carrying the translated `ERR_*`, and cache invalidation on success.
 *
 * Wrapped once so no screen has to remember either. A mutation that succeeds without
 * invalidating leaves the list it just changed showing the old rows, and the bug is
 * always reported as "it didn't save".
 */
export function useApiMutation<TVariables, TData = unknown>(
  fn: (variables: TVariables) => Promise<TData>,
  options: {
    // `readonly` on BOTH levels: the `keys` helpers return `as const` tuples, and a
    // plain `unknown[][]` rejects every one of them at the call site.
    invalidate?: readonly (readonly unknown[])[];
    successMessage?: string;
    onSuccess?: (data: TData, variables: TVariables) => void;
  } = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: fn,
    onSuccess: (data, variables) => {
      for (const key of options.invalidate ?? []) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      if (options.successMessage) toast.success(options.successMessage);
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      toast.error(friendlyError(error));
    },
  });
}

/** A typed `useQuery` for back-office lists, so screens do not repeat the generics. */
export function useAdminList<T>(
  resource: string,
  params: Record<string, unknown> = {},
  options?: Partial<UseQueryOptions<Paginated<T>>>,
) {
  return useQuery({
    queryKey: keys.admin.list(resource, params),
    queryFn: () => api.get<Paginated<T>>(`/admin/${resource}${qs(params)}`),
    placeholderData: (previous: Paginated<T> | undefined) => previous,
    ...options,
  });
}

export function useAdminOne<T>(resource: string, id: number | string | undefined, options?: Partial<UseQueryOptions<T>>) {
  return useQuery({
    queryKey: keys.admin.one(resource, id ?? 'none'),
    queryFn: () => api.get<T>(`/admin/${resource}/${id}`),
    enabled: id !== undefined && id !== 'new',
    ...options,
  });
}
